import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import Faq from "../components/Faq";
import InBlog from "../components/InBlog";
import ChatButton from "../components/ChatButton";

const STOP_WORDS = new Set([
  "the", "and", "but", "you", "are", "for", "that", "with", "was", "this", "have",
  "not", "from", "they", "she", "his", "her", "its", "had", "has", "were", "all",
  "there", "their", "what", "which", "when", "where", "how", "will", "shall",
  "can", "could", "would", "should", "may", "might", "just", "more", "some",
  "been", "any", "each", "very", "into", "then", "than", "them", "too", "such",
  "out", "our", "about", "over", "only", "off", "also", "if", "because", "while",
  "under", "again", "most", "own", "same", "do", "does", "did", "so", "no", "nor",
  "up", "down", "in", "on", "to", "of", "is", "it", "as", "at", "an", "be", "by", 
  "or", "a", "i", "we", "you", "your", "me", "my", "us", "one", "two", "three",
  "get", "go", "come", "see", "know", "take", "use", "make", "way", "time", "work",
  "part", "first", "last", "good", "great", "new", "old", "right", "left", "here",
  "now", "today", "yesterday", "tomorrow", "yes", "no", "true", "false", "big", "small"
]);

function KeywordDensityChecker() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const fetchAndAnalyze = async () => {
    setLoading(true);
    setResults(null);
    setError("");

    try {
      let fullUrl = url.trim();
      if (!/^https?:\/\//i.test(fullUrl)) {
        fullUrl = "https://" + fullUrl;
      }

      const candidates = [fullUrl];
      try {
        const alt = new URL(fullUrl);
        if (alt.hostname.startsWith("www.")) {
          alt.hostname = alt.hostname.replace(/^www\./, "");
        } else {
          alt.hostname = "www." + alt.hostname;
        }
        candidates.push(alt.toString());
      } catch (e) {}

      let success = false;
      for (const target of candidates) {
        try {
          const res = await fetch(
            `https://api.allorigins.win/get?url=${encodeURIComponent(target)}`
          );
          
          if (!res.ok) throw new Error('Network response was not ok');
          
          const data = await res.json();
          
          if (!data.contents) {
            throw new Error('No content received');
          }

          const parser = new DOMParser();
          const doc = parser.parseFromString(data.contents, "text/html");
          
          // Extract metadata
          const title = doc.querySelector("title")?.innerText || "";
          const description = doc.querySelector('meta[name="description"]')?.content || "";
          const h1 = doc.querySelector("h1")?.innerText || "";
          
          // Better content extraction
          const text = extractMainContent(doc);
          
          if (!text || text.trim().length < 50) {
            throw new Error('Insufficient content found');
          }

          const result = analyzeText(text, title, description, h1);
          setResults({ ...result, title, description, url: target });
          success = true;
          break;
        } catch (err) {
          console.warn(`Failed to fetch ${target}:`, err.message);
          continue;
        }
      }

      if (!success) {
        setError("Failed to fetch or analyze the page. Please check the URL and try again.");
      }
    } catch (err) {
      setError("An error occurred while analyzing the page.");
      console.error('Analysis error:', err);
    } finally {
      setLoading(false);
    }
  };

  const extractMainContent = (doc) => {
    // Remove unwanted elements
    const elementsToRemove = doc.querySelectorAll(
      'script, style, nav, header, footer, aside, .menu, .navigation, .sidebar, .ads, .advertisement, .social, .share, .comment'
    );
    elementsToRemove.forEach(el => el.remove());

    // Try to find main content area
    const contentSelectors = [
      'main',
      'article', 
      '[role="main"]',
      '.content',
      '.main-content',
      '#content',
      '.post-content',
      '.entry-content',
      '.article-content'
    ];

    let mainContent = null;
    for (const selector of contentSelectors) {
      mainContent = doc.querySelector(selector);
      if (mainContent) break;
    }

    // Fallback to body if no main content found
    if (!mainContent) {
      mainContent = doc.body;
    }

    return mainContent ? mainContent.innerText || "" : "";
  };

  const analyzeText = (text, title = "", description = "", h1 = "") => {
    // Clean and normalize text
    const cleanText = text
      .toLowerCase()
      .replace(/[^\w\s'-]/g, ' ') // Keep hyphens and apostrophes
      .replace(/\s+/g, ' ')
      .trim();

    // Extract words (minimum 3 characters, filter stop words and pure numbers)
    const words = cleanText
      .split(/\s+/)
      .filter(word => 
        word.length >= 3 && 
        !STOP_WORDS.has(word) && 
        !/^\d+$/.test(word) && // Filter pure numbers
        /^[a-z]/.test(word) // Must start with letter
      );

    const totalWords = words.length;
    
    // Count word frequencies
    const freq = {};
    words.forEach(word => {
      freq[word] = (freq[word] || 0) + 1;
    });

    // Also analyze 2-word phrases
    const phrases = [];
    for (let i = 0; i < words.length - 1; i++) {
      const phrase = `${words[i]} ${words[i + 1]}`;
      phrases.push(phrase);
    }

    const phraseFreq = {};
    phrases.forEach(phrase => {
      phraseFreq[phrase] = (phraseFreq[phrase] || 0) + 1;
    });

    // Prepare comparison strings
    const titleLower = title.toLowerCase();
    const descLower = description.toLowerCase();
    const h1Lower = h1.toLowerCase();

    // Analyze single words
    const singleWordDensity = Object.entries(freq)
      .filter(([word, count]) => count >= 2) // Only show words that appear at least twice
      .map(([word, count]) => {
        const rawDensity = (count / totalWords) * 100;
        const inTitle = titleLower.includes(word);
        const inDesc = descLower.includes(word);
        const inH1 = h1Lower.includes(word);
        
        // Calculate weighted density using multipliers
        let multiplier = 1;
        if (inTitle) multiplier *= 1.5;
        if (inDesc) multiplier *= 1.3;
        if (inH1) multiplier *= 1.4;
        
        const weightedDensity = rawDensity * multiplier;
        
        // Determine density level
        let densityLevel = 'low';
        if (rawDensity >= 3) densityLevel = 'high';
        else if (rawDensity >= 1) densityLevel = 'optimal';

        return {
          type: 'word',
          keyword: word,
          count,
          inTitle,
          inDesc,
          inH1,
          rawDensity: parseFloat(rawDensity.toFixed(2)),
          weightedDensity: parseFloat(weightedDensity.toFixed(2)),
          densityLevel
        };
      });

    // Analyze phrases (minimum 3 occurrences for phrases)
    const phraseDensity = Object.entries(phraseFreq)
      .filter(([phrase, count]) => count >= 3)
      .map(([phrase, count]) => {
        const rawDensity = (count / (totalWords - 1)) * 100; // Adjust for phrase calculation
        const inTitle = titleLower.includes(phrase);
        const inDesc = descLower.includes(phrase);
        const inH1 = h1Lower.includes(phrase);
        
        let multiplier = 1;
        if (inTitle) multiplier *= 1.6; // Higher weight for phrases in title
        if (inDesc) multiplier *= 1.4;
        if (inH1) multiplier *= 1.5;
        
        const weightedDensity = rawDensity * multiplier;
        
        let densityLevel = 'low';
        if (rawDensity >= 1.5) densityLevel = 'high';
        else if (rawDensity >= 0.5) densityLevel = 'optimal';

        return {
          type: 'phrase',
          keyword: phrase,
          count,
          inTitle,
          inDesc,
          inH1,
          rawDensity: parseFloat(rawDensity.toFixed(2)),
          weightedDensity: parseFloat(weightedDensity.toFixed(2)),
          densityLevel
        };
      });

    // Combine and sort by weighted density
    const allKeywords = [...singleWordDensity, ...phraseDensity]
      .sort((a, b) => b.weightedDensity - a.weightedDensity)
      .slice(0, 25); // Show top 25

    return { 
      totalWords, 
      totalUniqueWords: Object.keys(freq).length,
      keywords: allKeywords,
      topSingleWords: singleWordDensity.slice(0, 15),
      topPhrases: phraseDensity.slice(0, 10)
    };
  };

  const getDensityColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'optimal': return 'text-green-600 bg-green-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  const getDensityIcon = (level) => {
    switch (level) {
      case 'high': return 'bx-error-circle text-red-500';
      case 'optimal': return 'bx-check-circle text-green-500';
      default: return 'bx-info-circle text-blue-500';
    }
  };

  return (
    <>
      <Helmet>
        <title>Advanced Keyword Density Checker - Seotic</title>
        <meta name="description" content="Analyze keyword density with advanced metrics including phrases, weighted scoring, and SEO optimization tips" />
      </Helmet>
      <Navbar />
      <div className="px-5 bg-gradient-to-t from-slate-100 to-white">
        <div className="container mx-auto pt-20 pb-5">
          <div className="flex flex-col justify-center items-center mb-8">
            <h2 className="text-center text-base/7 uppercase text-slate-400">PUBLIC TOOL</h2>
            <p className="mx-auto mt-2 max-w-xl text-center text-4xl font-semibold tracking-tight text-pretty text-gray-950 sm:text-5xl">
              Advanced keyword density analyzer
            </p>
          </div>

          <p className="text-center text-slate-600 mb-8">
            Analyze single words and phrases with weighted scoring based on title, description, and H1 presence.
          </p>
          
          <div className="flex mb-6 max-w-md mx-auto">
            <input
              type="text"
              placeholder="https://www.example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchAndAnalyze()}
              className="flex-grow px-5 py-3 border border-slate-200 mr-2 rounded-lg focus:outline-none"
            />
            <button
              onClick={fetchAndAnalyze}
              disabled={loading || !url.trim()}
              className="px-5 py-3 bg-yellow-300 text-yellow-900 rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Analyzing...' : 'Analyze'}
            </button>
          </div>

          {loading && (
            <div className="text-center mt-10">
              
            <p className="mt-2">Analyzing...</p>
            </div>
          )}

          {error && (
            <div className="text-center mt-10 p-4 bg-red-50 border border-red-200 rounded-lg max-w-md mx-auto">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {results && (
            <div className="mt-10 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white border rounded-lg p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900">{results.totalWords}</div>
                  <div className="text-sm text-gray-500">Total Words</div>
                </div>
                <div className="bg-white border rounded-lg p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900">{results.totalUniqueWords}</div>
                  <div className="text-sm text-gray-500">Unique Words</div>
                </div>
                <div className="bg-white border rounded-lg p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900">{results.topSingleWords.length}</div>
                  <div className="text-sm text-gray-500">Keywords Found</div>
                </div>
                <div className="bg-white border rounded-lg p-6 text-center">
                  <div className="text-2xl font-bold text-gray-900">{results.topPhrases.length}</div>
                  <div className="text-sm text-gray-500">Phrases Found</div>
                </div>
              </div>

              {/* Page Info */}
              {(results.title || results.description) && (
                <div className="bg-white border rounded-lg p-6 hidden">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Page Information</h3>
                  {results.title && (
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-600">Title: </span>
                      <span className="text-sm text-gray-800">{results.title}</span>
                    </div>
                  )}
                  {results.description && (
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-600">Description: </span>
                      <span className="text-sm text-gray-800">{results.description}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-gray-600">URL: </span>
                    <span className="text-sm text-gray-800 break-all">{results.url}</span>
                  </div>
                </div>
              )}

              {/* Keywords Table */}
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b">
                  <h3 className="text-xl font-medium text-gray-900">Top Keywords & Phrases</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Sorted by weighted density (considering title, description, and H1 presence)
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Keyword/Phrase
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Count
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Raw %
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Weighted %
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Desc
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          H1
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {results.keywords.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{item.keyword}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              item.type === 'phrase' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {item.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                            {item.count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                            {item.rawDensity}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                            {item.weightedDensity}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {item.inTitle ? (
                              <i className="bx bx-check-circle text-green-500 text-lg"></i>
                            ) : (
                              <i className="bx bx-x text-gray-300 text-lg"></i>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {item.inDesc ? (
                              <i className="bx bx-check-circle text-green-500 text-lg"></i>
                            ) : (
                              <i className="bx bx-x text-gray-300 text-lg"></i>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {item.inH1 ? (
                              <i className="bx bx-check-circle text-green-500 text-lg"></i>
                            ) : (
                              <i className="bx bx-x text-gray-300 text-lg"></i>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getDensityColor(item.densityLevel)}`}>
                              <i className={`bx ${getDensityIcon(item.densityLevel)} mr-1`}></i>
                              {item.densityLevel}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Guide Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
            <div className="bg-white border rounded-lg p-10">
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Understanding Keyword Density</h3>
              <p className="font-medium tracking-tight text-slate-500 leading-[1.5em]">
                Keyword density shows how often a word appears relative to total content. Our tool analyzes both individual words and phrases, giving you a complete picture of your content's focus.
              </p>
            </div>

            

            <div className="bg-white border rounded-lg p-10">
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Optimization Tips</h3>
              <ul className="font-medium tracking-tight text-slate-500 leading-[2em]">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Target 1-2% density for main keywords
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Include keywords in title and H1
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Use natural variations and synonyms
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  Avoid keyword stuffing (>3% density)
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Focus on 2-3 word phrases for better targeting
                </li>
              </ul>
            </div>

            <div className="bg-white border rounded-lg p-10">
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Density Levels</h3>
              <div className="font-medium tracking-tight text-slate-500 space-y-3">
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-600 mr-3">
                    <i className="bx bx-info-circle mr-1"></i>
                    Low
                  </span>
                  <span className="text-sm text-gray-600">Under 1% - Consider increasing</span>
                </div>
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-50 text-green-600 mr-3">
                    <i className="bx bx-check-circle mr-1"></i>
                    Optimal
                  </span>
                  <span className="text-sm text-gray-600">1-3% - Well optimized</span>
                </div>
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-50 text-red-600 mr-3">
                    <i className="bx bx-error-circle mr-1"></i>
                    High
                  </span>
                  <span className="text-sm text-gray-600">Over 3% - Reduce usage</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <InBlog />
      <Faq />
      <div className="container mx-auto my-10">
        <ChatButton />
      </div>

      <Footer />
    </>
  );
}

export default KeywordDensityChecker;